import { useMemo } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ESG_CATEGORY_LABELS,
  SUSTAINABILITY_TRAJECTORY_CATEGORY_ORDER,
  orgTargetChartLabel,
  type GhgCategoryKey,
  type SustainabilityTrajectoryPoint,
} from '@siliconx/shared';
import { SX_PALETTE } from '@siliconx/shared/ui/charts';

const BORDER = 'hsl(var(--border, 240 5% 91%))';
const MUTED = 'hsl(var(--muted-foreground, 215 20% 65%))';

/** Category → series color. Operational categories take the violet family
 * (the forecast color used nowhere else for carbon, per SIL-349); the lump
 * categories take the remaining palette entries in a fixed, never-recycled
 * order so a category's color is stable across every render and screenshot. */
const CATEGORY_COLOR: Record<GhgCategoryKey, string> = {
  scope2: SX_PALETTE[0], // violet-600
  cat8: SX_PALETTE[1], // indigo-500
  cat1_or_2: SX_PALETTE[2], // sky-500
  cat5: SX_PALETTE[3], // emerald-500
  cat4: SX_PALETTE[4], // amber-500
};

/** Target line: black dashed, per the SIL-425 prototype-locked Variant A
 * ("black dashed target line") — deliberately NOT a palette hue, so it
 * never reads as one more emissions category. */
const TARGET_COLOR = '#18181b';

/**
 * SIL-442 (W4) hold-everything baseline trajectory — spec §6, Variant A's
 * category-stacked monthly area chart plus the black dashed org-target line.
 *
 * Pure presentational over the already-fetched
 * `GET /api/client/sustainability/trajectory` series: no arithmetic beyond
 * reshaping one recharts row per month. Every kgCO2e figure on this chart
 * was computed by the SIL-435 carbon kernel server-side — do not add a
 * derived figure here (a "vs target" gap, an annualization, a per-asset
 * average); those belong in the kernel where they can be unit-tested and
 * disclosed in the methodology sheet.
 *
 * ONLY non-zero categories are drawn. A hold-everything baseline has no
 * purchases, disposals, or moves by construction, so Cat 1/2, Cat 5 and
 * Cat 4 are structurally zero here — drawing four flat-zero bands would
 * imply four measured zeros. The caption in the parent card states this.
 */
export function SustainabilityTrajectoryChart({
  series,
  targetTco2ePerYr,
  height = 320,
}: {
  series: readonly SustainabilityTrajectoryPoint[];
  targetTco2ePerYr: number | null;
  height?: number;
}) {
  const rows = useMemo(
    () =>
      series.map((point) => ({
        month: point.month,
        ...point.categories,
        target: point.targetCumulativeKgco2e,
      })),
    [series],
  );

  /** Categories with any non-zero value anywhere in the horizon. */
  const drawnCategories = useMemo(
    () =>
      SUSTAINABILITY_TRAJECTORY_CATEGORY_ORDER.filter((key) =>
        series.some((point) => point.categories[key] !== 0),
      ),
    [series],
  );

  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center text-xs" style={{ height, color: MUTED }}>
        No months to project.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={rows} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
        <CartesianGrid stroke={BORDER} strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="month"
          tick={{ fontSize: 12, fill: MUTED }}
          tickLine={false}
          axisLine={{ stroke: BORDER }}
          label={{ value: 'Month', position: 'insideBottomRight', offset: -4, fontSize: 12, fill: MUTED }}
        />
        <YAxis
          tick={{ fontSize: 12, fill: MUTED }}
          tickLine={false}
          axisLine={{ stroke: BORDER }}
          width={72}
          tickFormatter={(v: number) => `${Math.round(v / 1000).toLocaleString('en-US')}t`}
        />
        <Tooltip
          formatter={(value: number, name: string) => [
            `${Math.round(value).toLocaleString('en-US')} kg CO2e`,
            name,
          ]}
          labelFormatter={(month: number) => `Month ${month}`}
          contentStyle={{ fontSize: 12, borderRadius: 8, borderColor: BORDER }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />

        {drawnCategories.map((key) => (
          <Area
            key={key}
            type="monotone"
            dataKey={key}
            name={ESG_CATEGORY_LABELS[key]}
            stackId="footprint"
            stroke={CATEGORY_COLOR[key]}
            fill={CATEGORY_COLOR[key]}
            fillOpacity={0.28}
            strokeWidth={1.5}
            isAnimationActive={false}
          />
        ))}

        {targetTco2ePerYr !== null && (
          <Line
            type="monotone"
            dataKey="target"
            name={orgTargetChartLabel(targetTco2ePerYr)}
            stroke={TARGET_COLOR}
            strokeWidth={1.75}
            strokeDasharray="6 4"
            dot={false}
            isAnimationActive={false}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
